import { IMPORT_KINDS, IMPORT_LIMITS, formatDateTime, type ImportKind } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import {
  IMPORT_STATUSES,
  importKeys,
  templateUrl,
  useImports,
  type ImportStatus,
  type ImportView,
} from '../../lib/imports-api.ts';
import { useSession } from '../../lib/session.ts';

const KIND_HELP: Record<ImportKind, string> = {
  contacts:
    'Tu cartera de clientes: nombre, teléfono o correo, distrito, papel y agente. Puedes crear también un negocio por contacto.',
  properties:
    'Tu inventario: título, tipo, operación, precio, distrito y propietario. Entran como borrador; las fotos se suben después en cada ficha. El Excel que exporta RECONNECT (RE/MAX) se sube tal cual: lo reconocemos solo.',
};

export const STATUS_TONES: Record<ImportStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  uploaded: 'info',
  analyzed: 'info',
  importing: 'warning',
  done: 'success',
  undone: 'neutral',
  cancelled: 'neutral',
  expired: 'neutral',
};

/** HU-CRM-06 / HU-INV-19: upload a file, and the history of imports. */
export function ImportsPage() {
  const imports = useImports();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Importar datos</h1>
        <p className="text-sm text-muted-foreground">
          Carga tu cartera y tu inventario desde Excel o CSV. Antes de crear nada revisas el mapeo
          de columnas y una vista previa con los duplicados y errores; durante 7 días puedes
          deshacer la importación.
        </p>
      </div>
      <NewImport />
      <Card className="space-y-3">
        <h2 className="font-semibold">Historial</h2>
        {imports.isPending ? (
          <Loading />
        ) : imports.error ? (
          <Alert tone="danger">{errorMessage(imports.error)}</Alert>
        ) : imports.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no hay importaciones.</p>
        ) : (
          <ul className="divide-y divide-border">
            {imports.data.map((i) => (
              <HistoryRow key={i.id} item={i} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function NewImport() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [kind, setKind] = useState<ImportKind>('contacts');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) return;
    if (file.size > IMPORT_LIMITS.maxBytes) {
      setError('El archivo pesa más de 5 MB. Divídelo en varios.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('kind', kind);
      body.append('file', file);
      const created = await api<ImportView>('/imports', { method: 'POST', body });
      client.setQueryData(importKeys.detail(created.id), created);
      await client.invalidateQueries({ queryKey: importKeys.list });
      void navigate(`/importaciones/${created.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Nueva importación</h2>
      <fieldset className="grid gap-2 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-medium">¿Qué vas a importar?</legend>
        {(Object.keys(IMPORT_KINDS) as ImportKind[]).map((k) => (
          <label
            key={k}
            className="flex cursor-pointer gap-3 rounded-lg border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-soft/40"
          >
            <input
              type="radio"
              name="kind"
              className="mt-0.5 size-5"
              checked={kind === k}
              onChange={() => {
                setKind(k);
              }}
            />
            <span className="text-sm">
              <span className="block font-medium">{IMPORT_KINDS[k]}</span>
              <span className="text-muted-foreground">{KIND_HELP[k]}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <a href={templateUrl(kind)} download className={buttonVariants({ variant: 'link' })}>
        <Download /> Descargar la plantilla de {IMPORT_KINDS[kind].toLowerCase()}
      </a>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void upload();
        }}
      >
        <div className="space-y-1.5">
          <p className="text-sm font-medium" id="import-file-label">
            Archivo
          </p>
          {/* Our own button: the native one shows the browser's language ("Choose File"). */}
          <div className="flex flex-wrap items-center gap-3">
            <input
              id="import-file"
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              aria-labelledby="import-file-label"
              aria-describedby="import-file-hint"
              className="peer sr-only"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError(null);
              }}
            />
            <label
              htmlFor="import-file"
              className={cn(
                buttonVariants({ variant: 'secondary' }),
                'cursor-pointer peer-focus-visible:outline-2 peer-focus-visible:outline-ring',
              )}
            >
              <FileSpreadsheet /> Elegir archivo
            </label>
            <span className="min-w-0 truncate text-sm text-muted-foreground">
              {file ? file.name : 'Ningún archivo elegido'}
            </span>
          </div>
          <p id="import-file-hint" className="text-sm text-muted-foreground">
            .xlsx o .csv, hasta 5 MB y {IMPORT_LIMITS.maxRows.toLocaleString('es-SV')} filas. La
            primera fila debe tener los títulos de las columnas. El archivo no se guarda.
          </p>
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" variant="brand" disabled={!file} loading={uploading}>
          <Upload /> Subir y revisar
        </Button>
      </form>
    </Card>
  );
}

function HistoryRow({ item }: { item: ImportView }) {
  const { data: session } = useSession();
  const c = item.counts;
  const parts = [
    c.created ? `${String(c.created)} creados` : null,
    c.valid ? `${String(c.valid)} listos` : null,
    c.duplicate ? `${String(c.duplicate)} duplicados` : null,
    c.error + c.failed ? `${String(c.error + c.failed)} con errores` : null,
    c.undone ? `${String(c.undone)} deshechos` : null,
  ].filter(Boolean);
  return (
    <li className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        <p className="flex flex-wrap items-center gap-2">
          <Link
            to={`/importaciones/${item.id}`}
            className="font-medium text-primary hover:underline"
          >
            {IMPORT_KINDS[item.kind]}
            {item.format === 'reconnect' ? ' de RECONNECT' : ''} · {item.totalRows} filas
          </Link>
          <Badge tone={STATUS_TONES[item.status]}>{IMPORT_STATUSES[item.status]}</Badge>
        </p>
        <p className="text-sm text-muted-foreground">
          {formatDateTime(item.createdAt, session?.tenant.timezone)} · {item.createdBy}
          {parts.length > 0 && ` · ${parts.join(', ')}`}
        </p>
      </div>
    </li>
  );
}
